import type {CustomError} from '@interfaces/error'

/**
 * Extracts a human-readable error message from various API error formats.
 * Handles RTK Query errors, Django REST framework errors, and standard Error objects.
 */
/**
 * Every string a DRF error payload carries, however deeply nested.
 *
 * Field paths are deliberately dropped. DRF's own messages name the field
 * ("description_template uses unknown placeholder(s) ..."), so prefixing them
 * with `actions.0.config` adds an index the author never saw and cannot act on.
 * Bounded depth so a pathological payload cannot recurse without end.
 */
const collectLeafMessages = (value: unknown, depth = 0): string[] => {
  if (depth > 5) return []
  if (typeof value === 'string') return [value]
  if (Array.isArray(value)) return value.flatMap((entry) => collectLeafMessages(entry, depth + 1))
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap((entry) =>
      collectLeafMessages(entry, depth + 1),
    )
  }
  return []
}

export const getApiErrorMessage = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') return null

  const err = error as Record<string, unknown>

  // RTK Query error format: { data: { non_field_errors: [...], field_name: [...] } }
  if (err.data && typeof err.data === 'object') {
    const data = err.data as Record<string, unknown>

    // Handle non_field_errors array
    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
      return data.non_field_errors.join(', ')
    }

    // Handle field-specific errors (e.g., { name: ["This field is required"] })
    const fieldErrors = Object.entries(data)
      .filter(([, value]) => Array.isArray(value))
      .map(([key, value]) => `${key}: ${(value as string[]).join(', ')}`)
    if (fieldErrors.length > 0) {
      return fieldErrors.join('; ')
    }

    // Handle detail message (DRF default, e.g. PermissionDenied)
    if (typeof data.detail === 'string') {
      return data.detail
    }

    // Handle custom { message: "..." } responses (DRF views/actions that return a
    // Response with a `message` key instead of `detail`).
    if (typeof data.message === 'string') {
      return data.message
    }

    // Nested serializer errors, e.g. a rule whose nested action config is
    // invalid: { actions: { "0": { config: ["..."] } } }. The branches above
    // only see a value that is itself an array, so anything a nested serializer
    // reports fell through to null and the caller showed its generic "save
    // failed" — which tells the author nothing about which field to fix.
    const nested = collectLeafMessages(data)
    if (nested.length > 0) {
      return nested.join(' ')
    }
  }

  // Handle standard Error object
  if (err.message && typeof err.message === 'string') {
    return err.message
  }

  return null
}

/**
 * When the backend blocks a signal→equipment assignment because it would exceed the
 * organization's allocated signal quota, it responds with
 * `{ machine_sub_system: ["…to {used}, exceeding its allocated quota of {allocated}."] }`.
 * Detect that specific error and return a fully translated message (the two counts are
 * parsed from the stable English backend message). Returns null for any other error so
 * callers can fall back to `getApiErrorMessage`.
 */
export const getSignalAllocationErrorMessage = (
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null => {
  if (!error || typeof error !== 'object') return null
  const data = (error as {data?: unknown}).data
  if (!data || typeof data !== 'object') return null
  const fieldError = (data as Record<string, unknown>).machine_sub_system
  const message = Array.isArray(fieldError) && fieldError.length > 0 ? String(fieldError[0]) : null
  if (!message || !/quota|alloc/i.test(message)) return null
  const counts = message.match(/\d+/g)
  if (!counts || counts.length < 2) return null
  return t('messages.signal_allocation_exceeded', {used: counts[0], allocated: counts[1]})
}

export const getDataPointErrorMessage = (error: unknown, t: (key: string) => string): string => {
  if (error instanceof Error) {
    return error.message
  }

  const customError = error as CustomError
  if (customError?.data?.address?.[0]) {
    return customError.data.address[0]
  }

  if (customError?.data?.non_field_errors?.[0]) {
    return t('data_points.address_already_exists')
  }

  return t('data_points.error_saving_data_point')
}
